import React, { useMemo } from "react";
import { getSeatStatus } from "../utils";

const STATUS_STYLES = {
  available:
    "border-emerald-500 bg-white text-slate-900 cursor-pointer hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_12px_28px_rgba(16,185,129,0.10)] disabled:hover:translate-y-0 disabled:hover:shadow-none",

  selected:
    "border-slate-900 bg-white text-slate-900 cursor-pointer shadow-[0_12px_28px_rgba(15,23,42,0.10)] ring-2 ring-slate-900/5",

  mine: "border-blue-200 bg-blue-50/50 text-blue-900",

  "my-permanent": "border-violet-200 bg-violet-50/60 text-violet-900",

  booked: "border-amber-200 bg-amber-50/60 text-amber-900",

  recurring: "border-yellow-300 bg-yellow-50 text-yellow-900",

  reserved: "border-[#d1d5db] bg-[#e5e7eb] text-[#6b7280]",

  permanent: "border-violet-300 bg-violet-50 text-violet-900",

  blocked:
    "border-[#ef4444] bg-[#fef2f2] text-[#991b1b] cursor-not-allowed",

  incompatible:
    "border-[#d1d5db] bg-white text-[#6b7280] cursor-not-allowed opacity-80",
};

const DOT_COLOR = {
  available: "bg-emerald-500",
  selected: "bg-emerald-500",
  mine: "bg-blue-600",
  "my-permanent": "bg-indigo-400",
  booked: "bg-amber-400",
  recurring: "bg-yellow-400",
  reserved: "bg-[#9ca3af]",
  blocked: "bg-[#ef4444]",
  incompatible: "bg-[#6b7280]",
  permanent: "bg-violet-500",
};

const STATUS_TEXT_STYLE = {
  available: "text-emerald-600",
  selected: "text-emerald-600",
  mine: "text-blue-600",
  "my-permanent": "text-indigo-500",
  permanent: "text-violet-700",
  booked: "text-amber-600",
  recurring: "text-yellow-700",
  reserved: "text-[#6b7280]",
  blocked: "text-[#ef4444]",
  incompatible: "text-[#6b7280]",
};

const STATUS_LABEL = {
  available: "Available",
  selected: "Available",
  booked: "Booked",
  recurring: "Recurring",
  reserved: "Taken",
  blocked: "Blocked",
  permanent: "Permanent",
  mine: "My booking",
  "my-permanent": "My permanent seat",
  incompatible: "Incompatible",
};

const UserSeatGrid = ({
  section,
  allBookings,
  availableSeats,
  blockedSeatIds,
  reservedSeatIds,
  permanentSeatIds,
  recurringSeatIds,
  selectedDate,
  currentUser,
  requirement,
  selectedSeatId,
  onSeatSelect,
  total,
  available,
}) => {
  const seatColumns = Number(section.seatColumns) || 6;
  const seatRowsCount = Number(section.seatRows) || 2;

  const sortedSeats = useMemo(
    () =>
      [...section.seats].sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.column - b.column;
      }),
    [section.seats],
  );

  const seatMatrix = useMemo(() => {
    const seatMap = new Map(
      sortedSeats.map((seat) => [`${seat.row}-${seat.column}`, seat]),
    );

    return Array.from({ length: seatRowsCount }, (_, rowIndex) =>
      Array.from({ length: seatColumns }, (_, columnIndex) => ({
        row: rowIndex + 1,
        column: columnIndex + 1,
        seat: seatMap.get(`${rowIndex + 1}-${columnIndex + 1}`) || null,
      })),
    );
  }, [seatColumns, seatRowsCount, sortedSeats]);

  return (
    <div>
      {/* Desktop View */}
      <div className="hidden lg:block">
        {/* Legend + Stats */}
        <div className="mb-4 lg:mb-6 flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left - Legend */}
          <div className="flex flex-wrap gap-x-4 sm:gap-x-6 lg:gap-x-7 gap-y-1.5 sm:gap-y-2 text-[9px] sm:text-[10px] lg:text-[11px] font-black">
            {[
              {
                dot: "bg-emerald-500",
                label: "Available",
              },
              {
                dot: "bg-blue-600",
                label: "My Booking",
              },
              {
                dot: "bg-[#9ca3af]",
                label: "Taken",
              },
              {
                dot: "bg-[#6b7280]",
                label: "Incompatible",
              },
              {
                dot: "bg-yellow-400",
                label: "Recurring",
              },
              {
                dot: "bg-violet-500",
                label: "Permanent",
              },
              {
                dot: "bg-[#ef4444]",
                label: "Blocked",
              },
            ].map((item) => (
              <span
                key={item.label}
                className="flex items-center gap-1.5 sm:gap-2 text-[12px] lg:gap-2.5 text-slate-500"
              >
                <span
                  className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${item.dot}`}
                />

                <span>{item.label}</span>
              </span>
            ))}
          </div>

          {/* Right - Seat Stats */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 lg:justify-end text-[9px] sm:text-[10px] lg:text-[11px] font-black tracking-[0.10em] sm:tracking-[0.12em] text-slate-500">
            <span>{section.seats.length} Seats Total</span>

            {total > 0 ? (
              <span
                className={`inline-flex border px-3 py-1 sm:px-4 sm:py-1.5 capitalize ${
                  available === 0
                    ? "border-rose-200 bg-rose-50 text-rose-600"
                    : available <= total * 0.3
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
              >
                {available}/{total} {requirement} available
              </span>
            ) : (
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 sm:px-4 sm:py-1.5 uppercase text-slate-500">
                No {requirement} seats
              </span>
            )}
          </div>
        </div>

        {/* Dynamic Seat Grid */}
        <div className="w-full overflow-x-auto lg:overflow-x-visible pb-2">
          <div className="flex min-w-full justify-center">
            <div
              className={`grid w-full gap-3 xl:gap-4 ${
                seatColumns <= 1
                  ? "grid-cols-1"
                  : seatColumns === 2
                    ? "grid-cols-1 sm:grid-cols-2"
                    : seatColumns === 3
                      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              }`}
              style={{
                gridTemplateColumns: `repeat(${seatColumns}, minmax(0, 1fr))`,
              }}
            >
              {seatMatrix
                .flat()
                .map(({ row, column, seat }) =>
                  seat ? (
                    <SeatCard
                      key={seat.id}
                      seat={seat}
                      allBookings={allBookings}
                      selectedDate={selectedDate}
                      currentUser={currentUser}
                      requirement={requirement}
                      availableSeats={availableSeats}
                      blockedSeatIds={blockedSeatIds}
                      reservedSeatIds={reservedSeatIds}
                      permanentSeatIds={permanentSeatIds}
                      recurringSeatIds={recurringSeatIds}
                      selectedSeatId={selectedSeatId}
                      onSeatSelect={onSeatSelect}
                      compact={false}
                    />
                  ) : (
                    <EmptySeatSlot
                      key={`empty-${row}-${column}`}
                      compact={false}
                    />
                  ),
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet View */}
      <div className="lg:hidden rounded-2xl border border-slate-100 bg-[#f8fafc] p-4 sm:p-5 shadow-[inset_0_1px_2px_rgba(0,0,0,0.02)]">
        <span className="block text-slate-400 text-[10px] sm:text-xs font-black uppercase tracking-[0.12em] mb-2.5 sm:mb-3">
          Click an available seat to select it.
        </span>

        {/* Legend + Stats */}
        <div className="mb-4 flex flex-col-reverse lg:flex-col gap-3">
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[9px] sm:text-[10px] font-black tracking-[0.12em]">
            {[
              {
                dot: "bg-emerald-500",
                label: "Available",
              },
              {
                dot: "bg-blue-600",
                label: "My Booking",
              },
              {
                dot: "bg-[#9ca3af]",
                label: "Taken",
              },
              {
                dot: "bg-[#6b7280]",
                label: "Incompatible",
              },
              {
                dot: "bg-yellow-400",
                label: "Recurring",
              },
              {
                dot: "bg-violet-500",
                label: "Permanent",
              },
              {
                dot: "bg-[#ef4444]",
                label: "Blocked",
              },
            ].map((item) => (
              <span
                key={item.label}
                className="flex items-center gap-1.5 text-slate-500 text-[10px]"
              >
                <span className={`h-2 w-2 rounded-full ${item.dot}`} />

                <span>{item.label}</span>
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-2 text-[9px] sm:text-[10px] font-black tracking-[0.10em] sm:tracking-[0.12em] text-slate-500">
            <span>{section.seats.length} Seats Total</span>

            {total > 0 ? (
              <span
                className={`inline-flex  border px-3 py-1 sm:px-4 sm:py-1.5 capitalize ${
                  available === 0
                    ? "border-rose-200 bg-rose-50 text-rose-600"
                    : available <= total * 0.3
                      ? "border-amber-200 bg-amber-50 text-amber-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
              >
                {available}/{total} {requirement} available
              </span>
            ) : (
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 sm:px-4 sm:py-1.5 uppercase text-slate-500">
                No {requirement} seats
              </span>
            )}
          </div>
        </div>

        {/* Horizontal Scroll Rows */}
        <div
          className="w-full overflow-x-auto pb-2 scrollbar-thin"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex min-w-full w-max flex-col gap-2.5 sm:gap-3">
            {seatMatrix.map((rowSeats, rowIdx) => (
              <div
                key={rowIdx}
                className="flex w-full justify-center gap-2.5 sm:gap-3 mt-4"
              >
                {rowSeats.map(({ row, column, seat }) =>
                  seat ? (
                    <SeatCard
                      key={seat.id}
                      seat={seat}
                      allBookings={allBookings}
                      selectedDate={selectedDate}
                      currentUser={currentUser}
                      requirement={requirement}
                      availableSeats={availableSeats}
                      blockedSeatIds={blockedSeatIds}
                      reservedSeatIds={reservedSeatIds}
                      permanentSeatIds={permanentSeatIds}
                      recurringSeatIds={recurringSeatIds}
                      selectedSeatId={selectedSeatId}
                      onSeatSelect={onSeatSelect}
                      compact={true}
                    />
                  ) : (
                    <EmptySeatSlot
                      key={`empty-${row}-${column}`}
                      compact={true}
                    />
                  ),
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────── */
/* Seat Card */
/* ────────────────────────────────────────────── */

const SeatCard = ({
  seat,
  allBookings,
  selectedDate,
  currentUser,
  requirement,
  availableSeats,
  blockedSeatIds,
  reservedSeatIds,
  permanentSeatIds,
  recurringSeatIds,
  selectedSeatId,
  onSeatSelect,
  compact,
}) => {
  const { status } = getSeatStatus(
    seat,
    allBookings,
    selectedDate,
    currentUser,
    requirement,
    availableSeats,
    blockedSeatIds,
    reservedSeatIds,
    permanentSeatIds,
    recurringSeatIds,
  );

  const isSelectedNow = selectedSeatId === seat.id;

  const effectiveStatus =
    isSelectedNow && status === "available" ? "selected" : status;

  const isClickable = status === "available";

  return (
    <button
      type="button"
      disabled={!isClickable}
      onClick={() => isClickable && onSeatSelect(seat.id)}
      aria-pressed={isSelectedNow}
      aria-label={`Seat ${seat.label} - ${STATUS_LABEL[effectiveStatus]}`}
      className={`relative text-left transition-all duration-200 ${
        compact
          ? "shrink-0 w-[110px] sm:w-[130px] border-[1.5px] p-2.5 sm:p-3"
          : "w-full min-w-0 border-[1px] p-3 lg:p-3.5 xl:p-4"
      } ${STATUS_STYLES[effectiveStatus] || STATUS_STYLES.incompatible}`}
    >
      <span className={`flex flex-col ${compact ? "gap-1" : "gap-2"}`}>
        {/* Top Content */}
        <span>
          <span className="flex items-start justify-between gap-1.5">
            {/* Seat Label */}
            <span
              className={`font-medium tracking-[-0.02em] leading-tight ${
                compact
                  ? "text-[0.8rem]"
                  : "text-[0.8rem] lg:text-[0.88rem] xl:text-[0.95rem]"
              }`}
            >
              {seat.label}
            </span>

            {/* Status Dot */}
            <span
              className={`mt-0.5 shrink-0 rounded-full ${
                compact ? "h-1.5 w-1.5" : "h-2 w-2"
              } ${DOT_COLOR[effectiveStatus] || DOT_COLOR.incompatible}`}
            />
          </span>

          {/* Seat Labels */}
          <span className="mt-1 flex flex-wrap gap-1">
            <span
              className={`inline-flex py-0.5 font-black text-slate-500 capitalize ${
                compact
                  ? "text-[8px] tracking-[0.10em]"
                  : "text-[8px] lg:text-[9px] xl:text-[10px] tracking-[0.10em] xl:tracking-[0.12em]"
              }`}
            >
              {seat.type}
            </span>
            {/* {(seat.restrictionType === "block" || blockedSeatIds?.includes(seat.id)) && (
              <span
                className={`inline-flex rounded-full border border-[#ef4444] bg-[#fef2f2] px-2 py-0.5 font-black text-[#ef4444] ${
                  compact
                    ? "text-[8px] tracking-[0.10em]"
                    : "text-[8px] lg:text-[9px] xl:text-[10px] tracking-[0.10em] xl:tracking-[0.12em]"
                }`}
              >
              </span>
            )} */}
          </span>
        </span>

        {/* Status Text */}
        <span
          className={`font-black ${
            compact
              ? "text-[8px] tracking-[0.04em]"
              : "text-[8px] lg:text-[9px] xl:text-[10px] tracking-[0.04em] xl:tracking-[0.06em]"
          } ${
            STATUS_TEXT_STYLE[effectiveStatus] || STATUS_TEXT_STYLE.incompatible
          }`}
        >
          {STATUS_LABEL[effectiveStatus] || "Incompatible"}
        </span>
      </span>
    </button>
  );
};

const EmptySeatSlot = ({ compact }) => (
  <div
    aria-hidden="true"
    className={`rounded-xl border border-dashed border-slate-200 bg-slate-50/70 ${
      compact
        ? "h-[68px] w-[110px] sm:h-[76px] sm:w-[130px] shrink-0"
        : "min-h-[108px] w-full min-w-0"
    }`}
  />
);

export default UserSeatGrid;
